/**
 * Tek bir araç için öneri oluşturur. (Optimize edilmiş versiyon)
 * @param {Object} order - Sipariş detayları.
 * @param {Array} vehicles - Araçlar listesi.
 * @param {Array} customers - Müşteriler listesi.
 * @param {Array} products - Ürünler listesi.
 * @param {boolean} [isBulk=false] - Toplu taşıma kontrolü.
 * @param {boolean} [skipTheTonnage=false] - Taşıma kapasitesini es geçme kontrolü.
 * @returns {Object|null} Önerilen araç veya null.
 */

const { calculateCustomerPriority } = require("./calculator/calculateCustomerPriority");
const { calculateProductTypePriority } = require("./calculator/calculateProductTypePriority");
const { calculateDistancePriority } = require("./calculator/calculateDistancePriority");
const { calculateEquipmentScore } = require("./calculator/calculateEquipmentScore");
const { isVehicleSuitableForOrder } = require("./controller/isVehicleSuitableForOrder");
const { isWashStatus } = require("./controller/isWashStatus");
const { isVehicleSuitableForOrderDetail } = require("./controller/isVehicleSuitableForOrderDetail");
const { checkCargoCompatibility } = require("./controller/checkCargoCompatibility");
const { haversineDistance } = require('./haversineDistance');
const { isSimilar } = require("./helpers/similarityHelper");
const {
  VEHICLE_CAPACITY,
  DISTANCE_LIMITS,
  PRIORITY_WEIGHTS,
  SIMILARITY_THRESHOLD
} = require("./constants");

const { checkForReallocation } = require("./recommendedModules/reallocate/index");
const { optimizeVehicles, addCloseBranchVehicles } = require("./recommendedModules/optimizeVehicles");
const processPolygonGroups = require("./recommendedModules/processPolygonGroups");
const calculateVehicleCosts = require("./recommendedModules/calculateVehicleCosts");
const preFilterVehicles = require("./recommendedModules/preFilterVehicles");
const { fetchAndCacheData } = require("../cost");



/**
 * Tek bir araç için öneri oluşturur. (Optimize edilmiş versiyon)
 * @param {Object} order - Sipariş detayları.
 * @param {Array} vehicles - Araçlar listesi.
 * @param {Array} customers - Müşteriler listesi.
 * @param {Array} products - Ürünler listesi.
 * @param {number} totalCount - Toplam önerilecek araç sayısı.
 * @param {boolean} [isBulk=false] - Toplu taşıma kontrolü.
 * @param {boolean} [skipTheTonnage=false] - Taşıma kapasitesini es geçme kontrolü.
 * @param {boolean} [setAvailable=true] - Araç durumunu güncelleme.
 * @param {Array} [blockedVehicles=[]] - Bloklanmış araçlar listesi.
 * @param {boolean} [isClose=false] - Yakın mesafe kontrolü.
 * @param {string} token - Kimlik doğrulama tokeni.
 * @param {Object} [customerExpectedProducts={}] - Müşteri beklenen ürünler (opsiyonel).
 * @param {Object} [vehicleInventories={}] - Araç envanterleri (opsiyonel).
 * @param {boolean} [checkTimeAvailability=false] - Rezervasyon zaman kontrolü yapılsın mı?
 * @param {string} [orderStartTime=null] - Siparişin başlangıç zamanı (YYYY-MM-DD HH:mm:ss).
 * @param {string} [orderEndTime=null] - Siparişin bitiş zamanı (YYYY-MM-DD HH:mm:ss).
 * @returns {Object|Array} Önerilen araç veya araçlar listesi.
 */
async function recommendSingleVehicleForOrder(
  order,
  vehicles,
  customers,
  products,
  totalCount,
  isBulk = false,
  skipTheTonnage = false,
  setAvailable = true,
  blockedVehicles = [],
  isClose = false,
  token,
  customerExpectedProducts = {},
  vehicleInventories = {},
  checkTimeAvailability = false,
  orderStartTime = null,
  orderEndTime = null
) {
  try {
    // vehicleInventories artık sabit katmandan gelecek, default {} olarak kalabilir

    // Eğer vehicleInventories boşsa, vehicles'dan otomatik oluştur
    if ((!vehicleInventories || Object.keys(vehicleInventories).length === 0) && vehicles && vehicles.length > 0) {
      const { getVehicleInventoryMap } = require('./helpers/getVehicleInventoryMap');
      vehicleInventories = getVehicleInventoryMap(vehicles);
    }

    // Eğer customerExpectedProducts boşsa ve token varsa, otomatik yükle
    if ((!customerExpectedProducts || Object.keys(customerExpectedProducts).length === 0) && token) {
      try {
        const { getCustomerExpectedProducts } = require('../detailData');
        customerExpectedProducts = await getCustomerExpectedProducts(token, false);
      } catch (error) {
        console.error("recommendSingleVehicleForOrder - customerExpectedProducts yüklenemedi:", error);
        customerExpectedProducts = {};
      }
    }

  // PERFORMANS OPTİMİZASYONU:
  // 1. Önce haversine ile tüm araçları hızlıca filtrele
  // 2. En yakın 50 tanesini al
  // 3. Sadece bu 50 araç için OSRM ile gerçek mesafe hesapla

  const { realRouteDistance } = require('./realRouteDistance');
  const { calculateDistancePriority } = require('./calculator/calculateDistancePriority');
  const { haversineDistance } = require('./haversineDistance');

  const cachedDistances = {};

  // Sipariş koordinatı
  const orderLocation = {
    lat: parseFloat(
      order?.startLat || order?.outpointLat || order?.fillingLatitude || 0
    ),
    lng: parseFloat(
      order?.startLng || order?.outpointLng || order?.fillingLongitude || 0
    )
  };

  // ADIM 1: Haversine ile ön filtreleme (hızlı)
  if (orderLocation.lat && orderLocation.lng && vehicles && vehicles.length > 0) {
    // Tüm araçlar için haversine mesafe hesapla
    const vehiclesWithHaversineDistance = vehicles.map(vehicle => {
      const fromLocation =
        vehicle?.orderNos?.length &&
        !vehicle.orderNos.includes(order.orderNo) &&
        vehicle?.planning
          ? {
              lat: parseFloat(vehicle.planning.targetPointLat || vehicle.latitude || 0),
              lng: parseFloat(vehicle.planning.targetPointLng || vehicle.longitude || 0)
            }
          : {
              lat: parseFloat(vehicle?.latitude || 0),
              lng: parseFloat(vehicle?.longitude || 0)
            };

      let haversine = Infinity;
      if (fromLocation.lat && fromLocation.lng) {
        haversine = haversineDistance(fromLocation, orderLocation);
      }

      return {
        ...vehicle,
        _haversineDistance: haversine
      };
    });

    // Haversine mesafeye göre sırala ve en yakın 50 tanesini al
    const TOP_N_FOR_OSRM = 50;
    let closestVehicles;

    // EKIPMAN BAZLI PLANLAMA: Ekipmanlı araçları dahil et
    if (order.requiredEquipmentCategories && order.requiredEquipmentCategories.length > 0) {
      const hasRequiredEquipment = (v) => {
        const inv = vehicleInventories[v.id] || [];
        const cats = {};
        inv.forEach(i => {
          if (i && i.category) {
            const isActive = i.status === 'AKTIF' || (i.current_location === 'ARACTA' && i.is_deleted === 0);
            if (isActive) {
              if (!cats[i.category]) cats[i.category] = [];
              cats[i.category].push(i);
            }
          }
        });
        return order.requiredEquipmentCategories.every(c => (cats[c] || []).length > 0);
      };

      const withEquipment = vehiclesWithHaversineDistance.filter(v =>
        v._haversineDistance !== Infinity && hasRequiredEquipment(v)
      );
      const withoutEquipment = vehiclesWithHaversineDistance
        .filter(v => v._haversineDistance !== Infinity && !hasRequiredEquipment(v))
        .sort((a, b) => a._haversineDistance - b._haversineDistance);

      // Ekipmanlı araçları her zaman dahil et, geri kalan slotları yakınlarla doldur
      const remainingSlots = Math.max(0, TOP_N_FOR_OSRM - withEquipment.length);
      closestVehicles = [...withEquipment, ...withoutEquipment.slice(0, remainingSlots)];
    } else {
      closestVehicles = vehiclesWithHaversineDistance
        .filter(v => v._haversineDistance !== Infinity)
        .sort((a, b) => a._haversineDistance - b._haversineDistance)
        .slice(0, TOP_N_FOR_OSRM);
    }

    // ADIM 2: Sadece en yakın 50 araç için OSRM ile gerçek mesafe hesapla
    if (closestVehicles.length > 0) {
      const distancePromises = closestVehicles.map(async (vehicle) => {
        const fromLocation =
          vehicle?.orderNos?.length &&
          !vehicle.orderNos.includes(order.orderNo) &&
          vehicle?.planning
            ? {
                lat: parseFloat(vehicle.planning.targetPointLat || vehicle.latitude || 0),
                lng: parseFloat(vehicle.planning.targetPointLng || vehicle.longitude || 0)
              }
            : {
                lat: parseFloat(vehicle?.latitude || 0),
                lng: parseFloat(vehicle?.longitude || 0)
              };

        if (fromLocation.lat && fromLocation.lng) {
          try {
            const distance = await realRouteDistance(fromLocation, orderLocation, true);
            if (vehicle.id) {
              cachedDistances[vehicle.id] = distance;
            }
          } catch (error) {
            // Hata durumunda haversine değeri kullanılacak
            if (vehicle.id && vehicle._haversineDistance !== Infinity) {
              cachedDistances[vehicle.id] = vehicle._haversineDistance;
            }
          }
        }
      });

      // OSRM hesaplamalarının tamamlanmasını bekle (max 3 saniye timeout)
      await Promise.race([
        Promise.allSettled(distancePromises),
        new Promise(resolve => setTimeout(resolve, 3000))
      ]);
    }
  }

  // PERFORMANS: Customers Map oluştur - O(n) yerine O(1) erişim
  const customersMap = new Map(
    customers.map(c => [c.name?.toLowerCase().trim(), c])
  );

  // PERFORMANS: Products Map oluştur - hızlı ürün erişimi
  const productsMap = new Map(
    products.map(p => [p.name?.toLowerCase().trim(), p])
  );

  // Uygunluk kontrolü için kapanış fonksiyonu
  const isSuitable = (vehicle) => {

    return isVehicleSuitableForOrder(
      order,
      vehicle,
      products,
      isBulk,
      skipTheTonnage,
      isClose
    );
  };

  // 1. Uygun araçları filtrele (cachedDistances ile gerçek yol mesafesi kullan)

  let suitableVehicles = preFilterVehicles(vehicles, order, isSuitable, DISTANCE_LIMITS.DEFAULT_AREA_SENSITIVITY, vehicleInventories, cachedDistances);

  if (suitableVehicles.length === 0) {
    // Ekipman gereksinimi varsa ve hiç uygun araç yoksa
    if (order.requiredEquipmentCategories && order.requiredEquipmentCategories.length > 0) {
      const categoryLabels = {
        'POMPA&HORTUM&DUSURUCU': 'Pompa & Hortum & Düşürücü',
        'GUVENLIK': 'Güvenlik Ekipmanı',
        'YEDEK_PARCA': 'Yedek Parça',
        'DIGER': 'Diğer'
      };

      const requiredEquipmentNames = order.requiredEquipmentCategories
        .map(cat => categoryLabels[cat] || cat)
        .join(', ');

      // TÜM FİLODA gerekli ekipmana sahip araç var mı kontrol et
      const vehiclesWithRequiredEquipment = vehicles.filter(vehicle => {
        const vehicleInventory = (vehicleInventories && vehicleInventories[vehicle.id]) || [];
        const vehicleCategoryMap = {};

        vehicleInventory.forEach(item => {
          if (!item || !item.category) return;
          if (!vehicleCategoryMap[item.category]) {
            vehicleCategoryMap[item.category] = [];
          }
          vehicleCategoryMap[item.category].push(item);
        });

        // Tüm gerekli kategoriler bu araçta var mı?
        return order.requiredEquipmentCategories.every(category => {
          const categoryItems = vehicleCategoryMap[category] || [];
          return categoryItems.length > 0;
        });
      });

      // Ekipmana sahip araçlar varsa ama hiçbiri uygun değilse
      if (vehiclesWithRequiredEquipment.length > 0) {
        return {
          message: `${requiredEquipmentNames} ekipmanına sahip araçlar sipariş koşullarını karşılamıyor.\n\nDorse tipi, tonaj veya mesafe uyumsuzluğu nedeniyle uygun araç bulunamadı.`,
          equipmentExistsButNotSuitable: true,
          requiredEquipment: requiredEquipmentNames,
          vehiclesWithEquipment: vehiclesWithRequiredEquipment.length
        };
      }

      // Ekipman filtresinden kaynaklanan hata
      // Yani: Sipariş koşullarına uygun araçlar var ama ekipmana sahip değiller
      if (suitableVehicles.equipmentFilterMetadata?.failedDueToEquipment) {
        const vehicleCount = suitableVehicles.equipmentFilterMetadata.vehiclesBeforeFilter;

        return {
          message: `${requiredEquipmentNames} ekipmanına sahip uygun araç bulunamadı.\n\n${vehicleCount} araç tonaj, dorse ve mesafe açısından uygun ancak gerekli ekipmana sahip değil.`,
          failedDueToEquipment: true,
          requiredEquipment: requiredEquipmentNames,
          suitableVehiclesWithoutEquipment: vehicleCount
        };
      }

      // Genel ekipman hatası
      return {
        message: `${requiredEquipmentNames} ekipmanı gereksinimi karşılanamadı.`,
        equipmentRequired: true,
        requiredEquipment: requiredEquipmentNames
      };
    }

    // Genel hata
    return { message: "Bu sipariş için uygun araç bulunamadı. Dorse tipi, tonaj kapasitesi veya mesafe uyumsuzluğu olabilir." };
  }

  // 2. Zaman müsaitliği kontrolü (eğer istenmişse)
  if (checkTimeAvailability && orderStartTime && orderEndTime) {
    const { filterAvailableVehicles } = require('./controller/isVehicleAvailableForTimeSlot');
    const timeAvailableVehicles = filterAvailableVehicles(suitableVehicles, orderStartTime, orderEndTime, order);

    if (timeAvailableVehicles.length === 0) {
      console.log('Tüm araçların rezervasyonu var, zaman uyumsuzluğu nedeniyle araç bulunamadı');
      return {
        message: "Belirtilen zaman aralığında müsait araç bulunamadı. Tüm uygun araçların rezervasyonu var.",
        hasTimeConflict: true,
        conflictCount: suitableVehicles.length
      };
    }

    console.log(`Zaman kontrolü: ${suitableVehicles.length} araçtan ${timeAvailableVehicles.length} tanesi müsait (sequential dahil)`);
    suitableVehicles = timeAvailableVehicles;
  }

  // 3. Bloklanmış araçları filtrele
  if (blockedVehicles?.length > 0) {
    const blockedPlates = new Set(blockedVehicles.map(v => v?.plate));
    suitableVehicles = suitableVehicles.filter(v => !blockedPlates.has(v?.plate));
  }

  // 3.5. Müşterinin kabul etmediği yük taşıyan araçları filtrele
  const customer = customers.find(
    (c) => isSimilar(c.name, order.customer, SIMILARITY_THRESHOLD.EXACT_MATCH)
  );

  if (customer?.rejected_loads) {
    const initialCount = suitableVehicles.length;

    suitableVehicles = suitableVehicles.filter(vehicle => {
      const vehicleLastProduct = vehicle?.isSequentialTrue ? vehicle?.productId : vehicle?.lastProduct;
      const cargoCheck = checkCargoCompatibility(
        vehicleLastProduct,
        customers,
        order.customer
      );

      // DEBUG: 01AYM399 ve 62AR899 için log
      if (order.orderNo == 45353 && (vehicle.plate === '01AYM399' || vehicle.plate === '62AR899')) {
        console.log(`🔍 Müşteri Yük Kontrolü - ${vehicle.plate}:`, {
          orderNo: order.orderNo,
          customer: order.customer,
          isSequentialTrue: vehicle.isSequentialTrue,
          vehicleLastProduct,
          lastProduct: vehicle.lastProduct,
          productId: vehicle.productId,
          cargoCheck,
          rejected_loads: customer.rejected_loads
        });
      }

      return cargoCheck.isCompatible; // Sadece uyumlu araçları bırak
    });

    const filteredCount = initialCount - suitableVehicles.length;
    if (filteredCount > 0) {
      console.log(`Müşteri yük uyumsuzluğu nedeniyle ${filteredCount} araç filtrelendi`);
    }
  }

  if (suitableVehicles.length === 0) {
    return {
      message: "Bu sipariş için Uygun araç bulunamadı.",
      incompatibilityIssue: true
    };
  }

  // 4. Araçları önceliklendirme (ekipman skoru dahil)
  const prioritizedVehicles = suitableVehicles.map(vehicle =>
    prioritizeVehicle(vehicle, order, customers, products, customerExpectedProducts, vehicleInventories, customersMap, productsMap)
  );

  const { costingData, washingPoints } = await fetchAndCacheData(token);
  // 5. Maliyet hesaplamaları
  const vehiclesWithCosts = await calculateVehicleCosts(prioritizedVehicles, order, token,{costingData, washingPoints});

  // 6. Polygon gruplarını işle ve sırala
  let processedVehicles = processPolygonGroups(vehiclesWithCosts);

  // Eğer iki arac aynı polygondaysa ve aynı scora sahipse ve maliyeti yüksek olan daha yukarıdaysa , maliyeti daha düşük olanı aşağı sıraya çekmek için önce maliyete göre sırala
  processedVehicles = processedVehicles.sort((a,b)=>a.costPriorityAverage-b.costPriorityAverage);

  // 7. Araçları önce müşteri yük uyumluluğuna, sonra ekipman uyumluluğuna, sonra costPriorityAverage ve polygonScore'a göre sırala
  const sortedVehicles = processedVehicles.sort((a, b) => {
    // Öncelik 0: Müşteri yük uyumluluğu - Uyumsuz yük taşıyan araçlar en sona
    const aIsIncompatible = a.recomendation?.isIncompatible === true;
    const bIsIncompatible = b.recomendation?.isIncompatible === true;

    if (aIsIncompatible && !bIsIncompatible) return 1;  // a uyumsuz, b uyumlu -> a aşağı
    if (!aIsIncompatible && bIsIncompatible) return -1; // a uyumlu, b uyumsuz -> a yukarı

    // Öncelik 1: Zorunlu ekipmanları tam karşılayan araçlar en üstte
    const aHasRequiredEquipment = a.recomendation?.equipmentCompatible === true && a.recomendation?.hasEquipmentRequirements === true;
    const bHasRequiredEquipment = b.recomendation?.equipmentCompatible === true && b.recomendation?.hasEquipmentRequirements === true;

    if (aHasRequiredEquipment && !bHasRequiredEquipment) return -1;
    if (!aHasRequiredEquipment && bHasRequiredEquipment) return 1;

    // Öncelik 2: Ekipman skoruna göre sırala (yüksek skor önce)
    if (a.recomendation?.hasEquipmentRequirements && b.recomendation?.hasEquipmentRequirements) {
      const equipmentScoreDiff = (b.recomendation?.equipmentScore || 0) - (a.recomendation?.equipmentScore || 0);
      if (Math.abs(equipmentScoreDiff) > 0.1) return equipmentScoreDiff;
    }

    // Öncelik 3: Maliyet ve polygon skoruna göre sırala
    const costDiff = a.costPriorityAverage - b.costPriorityAverage;
    if (costDiff !== 0) return costDiff;
    return b.polygonScore - a.polygonScore;
  });

  // 8. Optimal araçları seç ve gerekirse ek araçlar ekle
  let topVehicles = optimizeVehicles(sortedVehicles, order, totalCount, setAvailable, vehicles);

  // 9. Yakın şubedeki araçları ekle
  topVehicles = addCloseBranchVehicles(topVehicles, sortedVehicles);


  // 10. Uzak mesafe kontrolü
  // EKIPMAN BAZLI PLANLAMA: Reallocation kontrolünü atla
  if (order.requiredEquipmentCategories && order.requiredEquipmentCategories.length > 0) {
    return topVehicles;
  }

  const reallocationCheck = checkForReallocation(
    topVehicles,
    vehicles,
    order,
    products,
    300,
    isBulk,
    skipTheTonnage,
    isClose
  );

    // Eğer yeniden atama gerekliyse, bu sonucu döndür
    if (reallocationCheck) {
      return reallocationCheck;
    }


    // Normal sonucu döndür
    return topVehicles;

  } catch (error) {
    // 401 Authentication hatası
    if (error.isAuthError || (error.response && error.response.status === 401)) {
      console.error('🔴 401 Authentication Error in recommendSingleVehicleForOrder:', error.message);
      return {
        message: "Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.",
        error: "AUTHENTICATION_FAILED",
        statusCode: 401
      };
    }

    // Diğer hatalar
    console.error('❌ Error in recommendSingleVehicleForOrder:', error);
    return {
      message: "Araç önerisi oluşturulurken bir hata oluştu.",
      error: error.message || "UNKNOWN_ERROR"
    };
  }
}

/**
 * Bir aracı önceliklendiren yardımcı fonksiyon
 * @param {Object} vehicle - Araç
 * @param {Object} order - Sipariş
 * @param {Array} customers - Müşteriler
 * @param {Array} products - Ürünler
 * @param {Object} customerExpectedProducts - Müşteri beklenen ürünler
 * @param {Object} vehicleInventories - Araç envanterleri
 * @param {Map} customersMap - Müşteriler Map (performans için)
 * @param {Map} productsMap - Ürünler Map (performans için)
 * @returns {Object} Önceliklendirilmiş araç
 */
function prioritizeVehicle(vehicle, order, customers, products, customerExpectedProducts = {}, vehicleInventories = {}, customersMap = null, productsMap = null) {
  // Latitude ve longitude değerini polygon varsa güncelle
  const vehicleLocation = vehicle.polygonId ? {
    ...vehicle,
    latitude: vehicle.polygonLat,
    longitude: vehicle.polygonLng
  } : vehicle;
  
  // Öncelik değerlerini hesapla
  const distancePriority = calculateDistancePriority(order, vehicleLocation);

  // PERFORMANS: Map kullanarak müşteri önceliğini hesapla (O(1) erişim)
  const customerPriority = customersMap
    ? (customersMap.get(order.customer?.toLowerCase().trim())?.priority || 1)
    : calculateCustomerPriority(order, customers);

  const productTypePriority = calculateProductTypePriority(order, vehicle);
  const washPriority = isWashStatus(customers, products, vehicle, order);

  // Ekipman skorunu hesapla
  // order.requiredEquipmentCategories sadece "Ekipman ile Planla" seçildiğinde dolu olmalı
  const equipmentScore = calculateEquipmentScore(
    order,
    vehicle,
    customers,
    customerExpectedProducts,
    vehicleInventories,
    order.requiredEquipmentCategories || null
  );

  // Müşterinin kabul etmediği yük kontrolü
  const cargoCompatibility = checkCargoCompatibility(
    vehicle.lastProduct,
    customers,
    order.customer
  );

  // Yıkama durumuna göre öncelik değeri
  const washPriorityValue = washPriority.status == 2 ? PRIORITY_WEIGHTS.WASH_PENALTY : 1.0;

  // Uyumsuz yük varsa önceliği çok düşür (çok yüksek ceza faktörü ekle)
  // 1000.0 faktörü ile uyumsuz araçlar listenin en sonuna düşer
  const compatibilityPenalty = cargoCompatibility.isCompatible ? 1.0 : 1000.0;

  // Kombine öncelik hesaplama (ekipman skoru ve uyumluluk cezası dahil)
  const combinedPriority = (distancePriority/100).toFixed(3) *
    customerPriority *
    washPriorityValue *
    productTypePriority *
    equipmentScore.score *
    compatibilityPenalty;
  
  // Araç tipini kontrol et
  const isCentipede = vehicle.capacity >= VEHICLE_CAPACITY.CENTIPEDE_MIN && vehicle.capacity <= VEHICLE_CAPACITY.CENTIPEDE_MAX;
  
  // Ürün adını al
  const productName = order?.productName ? order?.productName : order?.productType;
  
  // Yakın şube kontrolü
  let isCloseBranch = false;
  if (vehicle.fleetBranch && vehicle.fleetBranch.length > 0) {
    const branch = vehicle.fleetBranch[0];
    const distance = haversineDistance(
      { lat: branch.latitude, lng: branch.longitude },
      { lat: order.endLat, lng: order.endLng }
    );
    if (distance < DISTANCE_LIMITS.CLOSE_BRANCH_DISTANCE) {
      isCloseBranch = true;
    }
  }
  const vehicleLastProduct = vehicle?.isSequentialTrue ? vehicle.productId : vehicle.lastProduct;
  return {
    ...vehicle,
    distance: distancePriority.toFixed(2),
    recomendation: {
      ...vehicle,
      ...isVehicleSuitableForOrderDetail(
        order,
        vehicle,
        products,
        false,
        false,
        isCentipede
      ),
      distancePriority: distancePriority.toFixed(2),
      customerPriority,
      capacity: (vehicle.capacity / 1000).toFixed(2),
      isWashing: washPriority.status,
      // Ekipman uyumluluk bilgileri
      equipmentCompatible: equipmentScore.compatible,
      equipmentScore: equipmentScore.score,
      hasEquipmentRequirements: equipmentScore.hasRequirements,
      matchedRequiredEquipment: equipmentScore.matchedRequired || [],
      missingRequiredEquipment: equipmentScore.missingRequired || [],
      insufficientRequiredEquipment: equipmentScore.insufficientRequired || [],
      missingOptionalEquipment: equipmentScore.missingOptional || [],
      insufficientOptionalEquipment: equipmentScore.insufficientOptional || [],
      equipmentDetails: equipmentScore.details,
      // Müşteri yük uyumluluk bilgileri
      isIncompatible: !cargoCompatibility.isCompatible,
      incompatibilityReason: cargoCompatibility.reason,
      rejectedProduct: cargoCompatibility.rejectedProduct
    },
    isCloseBranch,
    priority: combinedPriority,
    distance: parseFloat(distancePriority).toFixed(2),
    lastProductAvailable: !vehicleLastProduct
      ? 3
      : isSimilar(vehicleLastProduct, productName, SIMILARITY_THRESHOLD.CLOSE_MATCH)
        ? 1
        : 2,
  };
}

module.exports = {
  recommendSingleVehicleForOrder

};